import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Gauge, AlertTriangle, CheckCircle2, XCircle } from "lucide-react";
import AppLayout from "@/components/AppLayout";
import PageHeader from "@/components/PageHeader";
import KpiCard from "@/components/KpiCard";
import DataTable, { Column } from "@/components/DataTable";
import { LoadingState } from "@/components/LoadingState";
import { staffViewsApi } from "@/api/endpoints";
import { formatDateTime } from "@/utils/format";

interface MeterRow {
  id: string;
  meter_number: string;
  utility_type: string;
  status: string;
  organisation_id: string;
  organisation_name: string;
  building_id: string;
  building_name: string;
  unit_id: string;
  unit_number: string;
  last_reading_date: string | null;
  last_reading_value: number | null;
}

interface Summary {
  total: number;
  active: number;
  inactive: number;
  faulty: number;
  by_utility: Record<string, number>;
  no_recent_reading: number;
}

export default function StaffMetersPage() {
  const [rows, setRows] = useState<MeterRow[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>("all");

  useEffect(() => {
    Promise.all([staffViewsApi.meters(), staffViewsApi.metersSummary()]).then(([m, s]) => {
      setRows(m);
      setSummary(s);
      setLoading(false);
    });
  }, []);

  const filtered = filter === "all" ? rows
    : filter === "faulty" ? rows.filter((r) => r.status === "faulty")
    : filter === "inactive" ? rows.filter((r) => r.status === "inactive")
    : rows.filter((r) => r.utility_type === filter);

  const columns: Column<MeterRow>[] = [
    {
      key: "meter", header: "Meter",
      render: (m) => (
        <Link
          to={`/staff/organisations/${m.organisation_id}/buildings/${m.building_id}/units/${m.unit_id}`}
          className="text-brand-700 font-medium hover:underline"
        >
          {m.meter_number}
        </Link>
      ),
    },
    {
      key: "utility", header: "Utility",
      render: (m) => <span className="badge bg-slate-100 text-slate-700">{m.utility_type}</span>,
    },
    {
      key: "status", header: "Status",
      render: (m) =>
        m.status === "active" ? <span className="badge bg-brand-50 text-brand-800">Active</span>
        : m.status === "faulty" ? <span className="badge bg-red-50 text-red-800">Faulty</span>
        : <span className="badge bg-slate-100 text-slate-600">Inactive</span>,
    },
    {
      key: "org", header: "Organisation",
      render: (m) => (
        <Link to={`/staff/organisations/${m.organisation_id}`} className="text-slate-700 hover:underline">
          {m.organisation_name}
        </Link>
      ),
    },
    {
      key: "loc", header: "Location",
      render: (m) => <span className="text-xs text-slate-600">{m.building_name} · Unit {m.unit_number}</span>,
    },
    {
      key: "last", header: "Last reading",
      render: (m) => m.last_reading_date
        ? <span className="text-xs">{m.last_reading_value ?? "?"} on {formatDateTime(m.last_reading_date)}</span>
        : <span className="text-slate-400 text-xs">never</span>,
    },
  ];

  return (
    <AppLayout>
      <PageHeader title="Meters across all organisations" subtitle="Health, status, and recent reading activity" />
      {loading || !summary ? <LoadingState /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <KpiCard label="Total meters" value={summary.total} icon={<Gauge size={18} />} />
            <KpiCard label="Active" value={summary.active} icon={<CheckCircle2 size={18} />} accent="brand" />
            <KpiCard label="Faulty" value={summary.faulty} icon={<XCircle size={18} />} accent={summary.faulty ? "red" : "slate"} />
            <KpiCard
              label="No reading in 7+ days"
              value={summary.no_recent_reading}
              icon={<AlertTriangle size={18} />}
              accent={summary.no_recent_reading ? "amber" : "brand"}
            />
          </div>
          <div className="flex gap-2 mb-4 flex-wrap">
            {(["all","electricity","water","gas","faulty","inactive"] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1 rounded-full text-xs font-medium ${
                  filter === f ? "bg-brand-700 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
          <DataTable columns={columns} rows={filtered} emptyMessage="No meters match this filter" />
        </>
      )}
    </AppLayout>
  );
}
